import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'

import useMicLevelMeter from '../hooks/useMicLevelMeter'

// One bar occupies 3px plus the 2px gap that follows it, so the strip fills exactly at this pitch.
const BAR_PITCH_PX = 5
const MIN_BAR_COUNT = 12
// Only a sanity bound for absurd viewports. The meter's history is sized from the bar count rather
// than the other way round, so this no longer has to stay under a fixed analyser buffer — it just
// has to be high enough that a maximised widget on a wide screen keeps full bar density.
const MAX_BAR_COUNT = 400
const DEFAULT_BAR_COUNT = 32
// Fallback animation only: the delay wraps every WAVE_PERIOD bars so the pulse travels across.
const WAVE_PERIOD = 8

const barCountForWidth = (width: number): number => {
  if (!width) return DEFAULT_BAR_COUNT
  return Math.min(MAX_BAR_COUNT, Math.max(MIN_BAR_COUNT, Math.floor(width / BAR_PITCH_PX)))
}

interface NativeVoiceListeningIndicatorProps {
  /** The microphone is open and audio is being recorded. */
  isListening: boolean
  /** Recording has stopped and the audio is with the server. The bars stop reacting because there
   *  is no longer any sound to visualise, and the label says what is happening instead. */
  isTranscribing?: boolean
  className?: string
}

const NativeVoiceListeningIndicator: React.FC<NativeVoiceListeningIndicatorProps> = ({
  isListening,
  isTranscribing = false,
  className = '',
}) => {
  const intl = useIntl()

  const waveformRef = useRef<HTMLDivElement | null>(null)
  const barsRef = useRef<Array<HTMLDivElement | null>>([])
  const [barCount, setBarCount] = useState(DEFAULT_BAR_COUNT)

  // The strip is sized from its own measured width instead of a fixed bar count, so it fills the
  // composer at the same bar density whether the widget is docked, maximised or on a phone, and
  // re-fills every time the user toggles between those states mid-recording.
  useEffect(() => {
    const waveform = waveformRef.current
    if (!isListening || !waveform) return

    const applyWidth = (width: number) => {
      setBarCount((current) => {
        const next = barCountForWidth(width)
        return next === current ? current : next
      })
    }

    // Measure once up front so the first painted frame already matches the current panel size.
    applyWidth(waveform.getBoundingClientRect().width)

    if (typeof ResizeObserver === 'undefined') {
      // No ResizeObserver: viewport resizes are the only size change we can still react to.
      const onResize = () => applyWidth(waveform.getBoundingClientRect().width)
      window.addEventListener('resize', onResize)
      return () => window.removeEventListener('resize', onResize)
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      // contentRect can lag behind during a maximise/minimise transition; the live box is the
      // authority, so prefer a fresh read and fall back to the entry only if it is unavailable.
      const width = entry.target.getBoundingClientRect().width || entry.contentRect.width
      applyWidth(width)
    })
    observer.observe(waveform)
    return () => observer.disconnect()
  }, [isListening])

  const {isReactive} = useMicLevelMeter({enabled: isListening, barsRef})

  const registerBar = useCallback(
    (index: number) => (el: HTMLDivElement | null) => {
      barsRef.current[index] = el
    },
    []
  )

  const bars = useMemo(() => Array.from({length: barCount}, (_, i) => i), [barCount])

  useEffect(() => {
    barsRef.current.length = barCount
  }, [barCount])

  if (!isListening && !isTranscribing) return null

  const waveformClassName = `native-voice-listening-indicator__waveform${
    isReactive && !isTranscribing ? ' native-voice-listening-indicator__waveform--reactive' : ''
  }`

  const rootClassName = `native-voice-listening-indicator${
    isTranscribing ? ' native-voice-listening-indicator--transcribing' : ''
  } ${className}`.trim()

  const statusLabel = intl.formatMessage({
    id: isTranscribing ? 'CHAT.VOICE.TRANSCRIBING' : 'CHAT.VOICE.LISTENING',
  })

  return (
    <div
      className={rootClassName}
      role='status'
      aria-live='polite'
      aria-label={
        isTranscribing ? statusLabel : intl.formatMessage({id: 'CHAT.VOICE.LISTENING_STATUS'})
      }
    >
      <div ref={waveformRef} className={waveformClassName}>
        {bars.map((i) => (
          <div
            key={i}
            ref={registerBar(i)}
            className='native-voice-listening-indicator__wave-bar'
            style={
              isReactive && !isTranscribing
                ? undefined
                : {animationDelay: `${((i % WAVE_PERIOD) * 0.09).toFixed(2)}s`}
            }
          />
        ))}
      </div>

      <div className='native-voice-listening-indicator__text'>
        <span>{statusLabel}</span>
      </div>
    </div>
  )
}

export default React.memo(NativeVoiceListeningIndicator)
